<template>
  <section id="signIn">
    <SnackBar :SnackBarComponent="SnackBarComponent" />
    <div v-show="sign_in_dialog" class="sign-in-card">
      <div class="sign-in-card-glow" aria-hidden="true" />
      <div v-if="step === 3" class="sign-in-step-header">
        <div class="step-icon-wrap">
          <v-icon size="22" color="primary">mdi-lock-reset</v-icon>
        </div>
        <h2 class="sign-in-title">Forgot password?</h2>
        <p class="sign-in-subtitle">
          Enter your registered email and we'll send you a reset code.
        </p>
      </div>

      <div v-else-if="step === 4" class="sign-in-step-header">
        <div class="step-icon-wrap">
          <v-icon size="22" color="primary">mdi-email-check-outline</v-icon>
        </div>
        <h2 class="sign-in-title">Check your email</h2>
        <p class="sign-in-subtitle">
          Enter the code we sent and choose a new password.
        </p>
      </div>

      <div v-else-if="step === 2" class="sign-in-step-header">
        <div class="step-icon-wrap">
          <v-icon size="22" color="primary">mdi-lock-plus-outline</v-icon>
        </div>
        <h2 class="sign-in-title">{{ currentTitle }}</h2>
        <p class="sign-in-subtitle">Set a new password to continue.</p>
      </div>

      <div v-else-if="step !== 1 && step !== 5 && step !== 6" class="sign-in-step-header">
        <h2 class="sign-in-title">{{ currentTitle }}</h2>
      </div>

      <div v-else class="sign-in-header">
        <div class="sign-in-header-accent" aria-hidden="true" />
        <h2 class="sign-in-title">Welcome back</h2>
        <p class="sign-in-subtitle">Sign in to your PharmaWRK workspace</p>
      </div>

      <div
        v-if="step === 1 || step === 5 || step === 6"
        class="auth-mode-tabs"
      >
        <button
          type="button"
          class="auth-mode-tab"
          :class="{ 'auth-mode-tab--active': step === 1 }"
          @click="step = 1"
        >
          Sign In
        </button>
        <button
          type="button"
          class="auth-mode-tab"
          :class="{ 'auth-mode-tab--active': step === 5 || step === 6 }"
          @click="step = 5"
        >
          Login with OTP
        </button>
      </div>

      <v-window v-model="step" :touch="false" class="sign-in-window">
        <v-window-item :value="1">
          <v-form ref="form" lazy-validation class="sign-in-form">
            <div class="field-group">
              <label class="field-label">Email Address</label>
              <v-text-field
                maxlength="41"
                density="compact"
                placeholder="admin@example.com"
                autocomplete="off"
                variant="outlined"
                hide-details="auto"
                class="sign-in-field"
                prepend-inner-icon="mdi-email-outline"
                :rules="[
                  (v) => !!v || 'Email is Required',
                  (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                  (v) =>
                    (v && v.length < 50) ||
                    'Email must be less than 50 characters',
                ]"
                v-model="Login.EmailID"
              />
            </div>
            <div class="field-group">
              <div class="field-label-row">
                <label class="field-label">Password</label>
                <button
                  type="button"
                  class="forgot-link"
                  @click="step = 3"
                >
                  Forgot?
                </button>
              </div>
              <v-text-field
                maxlength="15"
                density="compact"
                autocomplete="off"
                placeholder="Enter your password"
                variant="outlined"
                hide-details="auto"
                class="sign-in-field"
                :type="isPwd ? 'text' : 'password'"
                @click:append-inner="isPwd = !isPwd"
                required
                :append-inner-icon="isPwd ? 'mdi-eye' : 'mdi-eye-off'"
                prepend-inner-icon="mdi-lock-outline"
                :rules="[
                  (v) => !!v || '',
                  (v) =>
                    (v && v.length < 15) ||
                    'Password must be less than 15 characters',
                  (v) =>
                    (v && v.length > 5) ||
                    'Password must be more than 5 characters',
                ]"
                v-model="Login.Password"
              />
            </div>
          </v-form>

          <div class="remember-row">
            <v-switch
              v-model="rememberme"
              color="primary"
              density="compact"
              hide-details
              :disabled="Login.EmailID == '' || Login.Password === ''"
              class="remember-switch"
            />
            <span class="remember-text">Remember me for 30 days</span>
          </div>

          <v-btn
            class="sign-in-btn gradient-bg text-white"
            block
            size="default"
            :loading="loading"
            rounded="pill"
            @click="ValidateLoginForm()"
          >
            Sign In
            <v-icon end size="18">mdi-arrow-right</v-icon>
          </v-btn>
        </v-window-item>
        <v-window-item :value="2">
          <v-form ref="formCP" lazy-validation class="sign-in-form">
            <div class="field-group">
              <label class="field-label">Enter New Password *</label>
              <v-text-field
                maxlength="15"
                class="sign-in-field"
                density="compact"
                autocomplete="off"
                variant="outlined"
                hide-details="auto"
                :type="isPwdNew ? 'text' : 'password'"
                @click:append-inner="isPwdNew = !isPwdNew"
                required
                :append-inner-icon="isPwdNew ? 'mdi-eye' : 'mdi-eye-off'"
                prepend-inner-icon="mdi-lock-outline"
                :rules="[
                  (v) => !!v || 'Required',
                  (v) =>
                    (v && v.length < 15) ||
                    'Password must be less than 15 characters',
                  (v) =>
                    (v && v.length > 5) ||
                    'Password must be more than 5 characters',
                ]"
                v-model="Password.NewPassword"
              />
            </div>
            <div class="field-group">
              <label class="field-label">Confirm New Password *</label>
              <v-text-field
                maxlength="15"
                class="sign-in-field"
                density="compact"
                autocomplete="off"
                variant="outlined"
                hide-details="auto"
                :type="isPwdConfirm ? 'text' : 'password'"
                @click:append-inner="isPwdConfirm = !isPwdConfirm"
                required
                :append-inner-icon="
                  isPwdConfirm ? 'mdi-eye' : 'mdi-eye-off'
                "
                prepend-inner-icon="mdi-lock-outline"
                v-model="Password.ConfirmPassword"
              />
            </div>
          </v-form>
          <v-btn
            block
            size="default"
            rounded="pill"
            :loading="loading"
            color="primary"
            class="sign-in-btn text-white mt-4"
            variant="elevated"
            @click="ValidateConfirmPasswordForm()"
          >
            Login
          </v-btn>
        </v-window-item>
        <v-window-item :value="3">
          <v-form ref="formOTP" class="sign-in-form">
            <div class="step-info-box">
              <v-icon size="18" color="primary" class="step-info-icon">mdi-information-outline</v-icon>
              <p class="step-info-text">
                Use the same email address you registered with for PharmaWRK.
              </p>
            </div>
            <div class="field-group">
              <label class="field-label">Registered email</label>
              <v-text-field
                maxlength="41"
                density="compact"
                variant="outlined"
                hide-details="auto"
                autocomplete="off"
                class="sign-in-field"
                placeholder="you@company.com"
                prepend-inner-icon="mdi-email-outline"
                v-model="OTPEmail"
                :rules="[
                  (v) => !!v || 'Email is Required',
                  (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                  (v) =>
                    (v && v.length < 50) ||
                    'Email must be less than 50 characters',
                ]"
              />
            </div>
          </v-form>
          <v-btn
            class="sign-in-btn gradient-bg text-white"
            block
            size="default"
            rounded="pill"
            @click="ValidateOTP()"
          >
            Send reset code
            <v-icon end size="18">mdi-arrow-right</v-icon>
          </v-btn>
          <button type="button" class="step-back-link" @click="step = 1">
            <v-icon size="16">mdi-arrow-left</v-icon>
            Back to sign in
          </button>
        </v-window-item>
        <v-window-item :value="4">
          <v-form ref="formConfirmOTP" class="sign-in-form">
            <div class="step-info-box">
              <v-icon size="18" color="primary" class="step-info-icon">mdi-shield-key-outline</v-icon>
              <p class="step-info-text">
                Check your inbox for the activation code, then set your new password below.
              </p>
            </div>
            <div class="field-group">
              <label class="field-label">Activation code</label>
              <v-text-field
                density="compact"
                variant="outlined"
                hide-details="auto"
                autocomplete="off"
                class="sign-in-field"
                placeholder="Enter 6-digit code"
                prepend-inner-icon="mdi-numeric"
                v-model="ConfirmOTP"
                :rules="[(v) => !!v || 'Code is required']"
              />
            </div>
            <div class="field-group">
              <label class="field-label">New password</label>
              <v-text-field
                maxlength="15"
                density="compact"
                autocomplete="off"
                variant="outlined"
                hide-details="auto"
                class="sign-in-field"
                placeholder="Min. 6 characters"
                v-model="ConfirmNewPassword"
                :rules="[
                  (v) => !!v || '',
                  (v) =>
                    (v && v.length < 15) ||
                    'Password must be less than 15 characters',
                  (v) =>
                    (v && v.length > 5) ||
                    'Password must be more than 5 characters',
                ]"
                :type="isPwdConfirmCode ? 'text' : 'password'"
                @click:append-inner="isPwdConfirmCode = !isPwdConfirmCode"
                required
                :append-inner-icon="
                  isPwdConfirmCode ? 'mdi-eye' : 'mdi-eye-off'
                "
                prepend-inner-icon="mdi-lock-outline"
              />
            </div>
          </v-form>
          <v-btn
            class="sign-in-btn gradient-bg text-white"
            block
            size="default"
            rounded="pill"
            @click="ValidateEnteredOTP()"
          >
            Update password
            <v-icon end size="18">mdi-check</v-icon>
          </v-btn>
          <button type="button" class="step-back-link" @click="step = 1">
            <v-icon size="16">mdi-arrow-left</v-icon>
            Back to sign in
          </button>
        </v-window-item>
        <v-window-item :value="5">
          <div class="step-info-box step-info-box--otp">
            <v-icon size="18" color="primary" class="step-info-icon">mdi-cellphone-message</v-icon>
            <p class="step-info-text">
              We'll email a one-time code to your registered work email. No password needed.
            </p>
          </div>
          <v-form ref="EmailOTP" class="sign-in-form">
            <div class="field-group">
              <label class="field-label">Work email</label>
              <v-text-field
                density="compact"
                variant="outlined"
                hide-details="auto"
                class="sign-in-field"
                placeholder="you@company.com"
                prepend-inner-icon="mdi-email-outline"
                v-model="Login_OTP_Email"
                :rules="[
                  (v) => !!v || 'Email is Required',
                  (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                  (v) =>
                    (v && v.length < 50) ||
                    'Email must be less than 50 characters',
                ]"
              />
            </div>
          </v-form>
          <v-btn
            class="sign-in-btn gradient-bg text-white"
            block
            size="default"
            rounded="pill"
            :loading="Emailloading"
            @click="ValidateEmailOTPForm()"
          >
            Send OTP
            <v-icon end size="18">mdi-send-outline</v-icon>
          </v-btn>
          <button type="button" class="step-back-link" @click="step = 1">
            <v-icon size="16">mdi-arrow-left</v-icon>
            Back to sign in
          </button>
        </v-window-item>
        <v-window-item :value="6">
          <div class="step-info-box">
            <v-icon size="18" color="primary" class="step-info-icon">mdi-shield-check-outline</v-icon>
            <p class="step-info-text">
              Enter the 6-digit code sent to
              <span class="step-info-email">{{ $store.getters.get_user_email }}</span>
              <button type="button" class="step-change-link" @click="step = 5">Change</button>
            </p>
          </div>
          <v-form ref="formOTPForm" lazy-validation class="sign-in-form">
            <v-otp-input
              density="compact"
              length="6"
              class="otp-input-field"
              @click:append="isPwdNew = !isPwdNew"
              required
              :rules="[(v) => !!v || 'Password is required']"
              v-model="OTPRecieved"
            />
          </v-form>
          <v-btn
            block
            size="default"
            rounded="pill"
            :loading="loadingPassword"
            color="primary"
            class="sign-in-btn gradient-bg text-white"
            @click="ValidateConfirmSignIn()"
          >
            Verify and sign in
            <v-icon end size="18">mdi-arrow-right</v-icon>
          </v-btn>
        </v-window-item>
      </v-window>
    </div>
  </section>
</template>

<script>
import {
  signIn,
  resetPassword,
  confirmSignIn,
  confirmResetPassword,
  signOut,
} from "aws-amplify/auth";
import SnackBar from "@/components/Extras/SnackBar.vue";
export default {
  props: {
    sign_in_dialog: Boolean,
  },
  components: {
    SnackBar,
  },
  data: () => ({
    step: 1,
    Login: {
      EmailID: "",
      Password: "",
    },
    ConfirmOTP: "",
    Login_OTP_Email: "",
    ConfirmNewPassword: "",
    OTPEmail: "",
    Emailloading: false,
    isPwdNew: false,
    isPwdConfirm: false,
    isPwdConfirmCode: false,
    Password: {
      NewPassword: "",
      ConfirmPassword: "",
    },
    isPwd: false,
    loading: false,
    rememberme: false,
    SnackBarComponent: {},
    OTPRecieved: "",
    loadingPassword: false,
  }),
  computed: {
    currentTitle() {
      switch (this.step) {
        case 1:
          return "Sign In";
        case 2:
          return "Change Temporary Password";
        case 3:
          return "Forgot Password ?";
        case 4:
          return "Forgot Password ?";
        case 5:
          return "Sign Up";
        default:
          return "Account created";
      }
    },
  },
  watch: {
    // rememberme(val) {
    //   if (val == true) {
    //     localStorage.setItem("currentuseremail", JSON.stringify(this.Login));
    //   } else if (val == false) {
    //     delete localStorage.currentuseremail;
    //   }
    // },
    rememberme(val) {
      if (val) {
        localStorage.setItem("remembered_email", this.Login.EmailID);
      } else {
        localStorage.removeItem("remembered_email");
      }
    },
  },
  mounted() {
    this.CheckRememberMe();
  },
  methods: {
    // CheckRememberMe() {
    //   if (localStorage.getItem("currentuseremail") != null) {
    //     this.Login = JSON.parse(localStorage.getItem("currentuseremail"));
    //     this.rememberme = true;
    //   }
    // },
    CheckRememberMe() {
      const savedEmail = localStorage.getItem("remembered_email");
      if (savedEmail) {
        this.Login.EmailID = savedEmail;
        this.rememberme = true;
      }
    },
    ConfirmOTPMethod() {
      confirmResetPassword({
        username: this.OTPEmail,
        confirmationCode: this.ConfirmOTP,
        newPassword: this.ConfirmNewPassword,
      })
        .then(() => {
          this.$refs.formConfirmOTP.reset();
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "Password Successfully Changed ",
            timeout: 2000,
          };
          this.step = 1;
        })
        .catch((err) => {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            Top: true,
            SnackbarText: err.message,
            timeout: 2000,
          };
        });
    },
    async ValidateEnteredOTP() {
      (this.OTPEmail = this.OTPEmail.trim()),
        (this.ConfirmOTP = this.ConfirmOTP.trim()),
        (this.ConfirmNewPassword = this.ConfirmNewPassword.trim());
      const FormValidate = await this.$refs.formConfirmOTP.validate();
      if (FormValidate.valid) {
        this.ConfirmOTPMethod();
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: "Please Enter The Code Sent To Registered Email",
          timeout: 2000,
        };
      }
    },
    async ValidateOTP() {
      this.OTPEmail = this.OTPEmail.trim();
      const FormValidate = await this.$refs.formOTP.validate();
      if (FormValidate.valid) {
        if (this.OTPEmail != "" && this.OTPEmail != null) {
          this.SendOTPEmail();
        }
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: "Please Enter Registered Email",
          timeout: 2000,
        };
      }
    },
    SendOTPEmail() {
      resetPassword({ username: this.OTPEmail })
        .then(() => {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "OTP Sent To Registered Email",
            timeout: 2000,
          };
          this.step = 4;
        })
        .catch((err) => {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            Top: true,
            SnackbarText: err.message,
          };
        });
    },
    // async ConfirmPassword() {
    //   try {
    //     this.loading = true;
    //     const result = await completeNewPassword(
    //       this.user,
    //       this.Password.ConfirmPassword,
    //     )
    //       .then((user) => {
    //         return user;
    //       })
    //       .catch((e) => {
    //         return e;
    //       });
    //     if (result.username) {
    //       this.$refs.formCP.reset();
    //       this.loading = false;
    //       this.SnackBarComponent = {
    //         SnackbarVmodel: true,
    //         SnackbarColor: "green",
    //         Top: true,
    //         SnackbarText: "Login Successful",
    //         timeout: 2000,
    //       };
    //       this.ActivateMethod();
    //     }
    //     this.loading = false;
    //   } catch (error) {
    //     this.loading = false;
    //     this.SnackBarComponent = {
    //       SnackbarVmodel: true,
    //       SnackbarColor: "red",
    //       Top: true,
    //       SnackbarText: error.message,
    //       timeout: 2000,
    //     };
    //   }
    // },
    async confirmNewPassword(newPassword) {
      try {
        await confirmSignIn({
          challengeResponse: newPassword,
        });

        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "green",
          SnackbarText: "Password updated successfully",
          timeout: 2000,
        };
        this.Login.EmailID = this.Login.EmailID.toLowerCase();
        this.Login.Password = newPassword;
        this.signInMethod();
      } catch (err) {
        console.error(err);
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          SnackbarText: "Failed to set new password",
          timeout: 2000,
        };
      }
    },
    async ValidateConfirmPasswordForm() {
      this.Password.NewPassword = this.Password.NewPassword.trim();
      this.Password.ConfirmPassword = this.Password.ConfirmPassword.trim();
      const FormValidate = await this.$refs.formCP.validate();
      if (FormValidate.valid) {
        if (
          this.Password.NewPassword != "" &&
          this.Password.NewPassword != null &&
          this.Password.ConfirmPassword != "" &&
          this.Password.ConfirmPassword != null
        ) {
          if (this.Password.NewPassword == this.Password.ConfirmPassword) {
            this.confirmNewPassword(this.Password.NewPassword);
          } else {
            this.$refs.form.reset();
            this.SnackBarComponent = {
              SnackbarVmodel: true,
              SnackbarColor: "red",
              Top: true,
              SnackbarText: "Password does not match",
              timeout: 2000,
            };
          }
        }
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: "Fields marked with asterisks (*) are mandatory",
          timeout: 2000,
        };
      }
    },
    async ValidateLoginForm() {
      (this.Login.EmailID = this.Login.EmailID.trim()),
        (this.Login.Password = this.Login.Password.trim());
      const FormValidate = await this.$refs.form.validate();
      if (FormValidate.valid) {
        if (
          this.Login.EmailID != "" &&
          this.Login.EmailID != null &&
          this.Login.Password != "" &&
          this.Login.Password != null
        ) {
          this.signInMethod();
        }
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: "Fields marked with asterisks (*) are mandatory",
          timeout: 2000,
        };
      }
    },
    // async signInMethod() {
    //   try {
    //     this.loading = true;
    //     const { isSignedIn, nextStep } = await signIn({
    //       username: this.Login.EmailID,
    //       password: this.Login.Password,
    //     });
    //     console.log("Check.....!", isSignedIn, nextStep);
    //     this.$store.commit("SET_USEREMAIL", this.Login.EmailID.toLowerCase());

    //     this.SnackBarComponent = {
    //       SnackbarVmodel: true,
    //       SnackbarColor: "primary",
    //       SnackbarText: "Login Successful",
    //       timeout: 2000,
    //     };

    //     this.ActivateMethod();
    //   } catch (error) {
    //     console.log("error signing in", error);
    //     if (error.message == "Incorrect username or password.") {
    //       this.loading = false;
    //       this.SnackBarComponent = {
    //         SnackbarVmodel: true,
    //         SnackbarColor: "red",
    //         SnackbarText: error.message,
    //         timeout: 2000,
    //       };
    //     }
    //     if (error.message == "There is already a signed in user.") {
    //       await signOut();
    //       this.signInMethod();
    //     }
    //   }
    // },

    async signInMethod() {
      try {
        this.loading = true;

        const { isSignedIn, nextStep } = await signIn({
          username: this.Login.EmailID,
          password: this.Login.Password,
        });

        console.log("Check.....!", isSignedIn, nextStep);

        // ✅ Case 1 — Normal Login
        if (isSignedIn) {
          this.$store.commit("SET_USEREMAIL", this.Login.EmailID.toLowerCase());

          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "primary",
            SnackbarText: "Login Successful",
            timeout: 2000,
          };

          this.loading = false;
          this.ActivateMethod();
          return;
        }

        // ✅ Case 2 — Cognito requires NEW PASSWORD
        if (
          nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          this.loading = false;

          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "orange",
            SnackbarText: "You must set a new password",
            timeout: 2000,
          };

          this.step = 2;

          return;
        }
      } catch (error) {
        console.log("error signing in", error);

        if (error.message === "Incorrect username or password.") {
          this.loading = false;
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            SnackbarText: error.message,
            timeout: 2000,
          };
        }

        if (error.message === "There is already a signed in user.") {
          await signOut();
          this.signInMethod();
        }
      }
    },
    ActivateMethod() {
      setTimeout(() => {
        this.$router.push("/LandingPage");
        this.loading = false;
      }, 1000);
      this.overlay = false;
    },
    SignInDialogEmit() {
      this.loading = false;
      this.step = 1;
      this.$emit("clicked");
    },
    async ValidateEmailOTPForm() {
      this.Login_OTP_Email = this.Login_OTP_Email.trim();
      const FormValidate = await this.$refs.EmailOTP.validate();
      if (FormValidate.valid) {
        if (this.Login_OTP_Email != "" && this.Login_OTP_Email != null) {
          this.SignInOTPEmail();
        }
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: "Fields marked with asterisks (*) are mandatory",
        };
      }
    },
    async SignInOTPEmail() {
      try {
        this.Emailloading = true;
        const clientMetadata = {
          suppressEmail: "true",
        };
        this.user = await signIn(this.Login_OTP_Email);
        this.$store.commit("SET_USEREMAIL", this.Login_OTP_Email.toLowerCase());
        if (this.user.challengeName === "CUSTOM_CHALLENGE") {
          this.OTPRecieved = "";
          this.step = 6;
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "OTP Sent Successfully!!",
          };
        }
        this.Emailloading = false;
      } catch (error) {
        console.log("error", error);
        this.Emailloading = false;
        if (
          error ==
          "UserLambdaValidationException: DefineAuthChallenge failed with error Error: NOT_AUTHORIZED : Kindly Sigup."
        ) {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            Top: true,
            // SnackbarText: error.message,
            SnackbarText: "Email not found",
          };
        } else {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            Top: true,
            // SnackbarText: error.message,
            SnackbarText: "Invalid Credentials",
          };
        }
      }
    },
    async ValidateConfirmSignIn() {
      const FormValidate = await this.$refs.formOTPForm.validate();
      if (FormValidate.valid) {
        this.confirmOTP();
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: "Fields marked with asterisks (*) are mandatory",
        };
      }
    },
    async confirmOTP() {
      try {
        this.loadingPassword = true;
        await sendCustomChallengeAnswer(this.user, this.OTPRecieved).then(
          (res) => {
            console.log("Check The Result", res);
            if (res.attributes.email == undefined) {
              this.loadingPassword = false;
              this.SnackBarComponent = {
                SnackbarVmodel: true,
                SnackbarColor: "red",
                Top: true,
                SnackbarText: "Invalid OTP Entered",
              };
              this.loadingPassword = false;
            } else {
              this.ActivateMethod();
              this.loadingPassword = false;
            }
          },
        );

        console.log("Hello");
      } catch (error) {
        console.log("errorrr", error);
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: error.message,
        };
        this.loadingPassword = false;
      }
    },
  },
};
</script>

<style scoped>
.sign-in-card {
  background: rgba(255, 255, 255, 0.52);
  backdrop-filter: blur(40px) saturate(200%);
  -webkit-backdrop-filter: blur(40px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 20px;
  padding: 28px 28px 24px;
  box-shadow:
    0 16px 48px rgba(12, 80, 55, 0.1),
    0 4px 16px rgba(12, 159, 107, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    inset 0 -1px 0 rgba(255, 255, 255, 0.25);
  position: relative;
  overflow: hidden;
}

.sign-in-card-glow {
  position: absolute;
  top: -60px;
  right: -60px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(15, 197, 102, 0.2) 0%,
    transparent 70%
  );
  pointer-events: none;
  z-index: 0;
}

.sign-in-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 55%;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.45) 0%,
    transparent 100%
  );
  pointer-events: none;
  border-radius: 20px 20px 0 0;
}

.sign-in-card::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 24px;
  right: 24px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(12, 159, 107, 0.15) 50%,
    transparent 100%
  );
  pointer-events: none;
}

.sign-in-header {
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.sign-in-header-accent {
  width: 32px;
  height: 3px;
  border-radius: 3px;
  background: linear-gradient(90deg, #0fc566 0%, #0a876e 100%);
  margin-bottom: 12px;
}

.sign-in-step-header {
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.step-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(12, 159, 107, 0.1);
  border: 1px solid rgba(12, 159, 107, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.step-info-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 20px;
  border-radius: 14px;
  background: rgba(12, 159, 107, 0.08);
  border: 1px solid rgba(12, 159, 107, 0.14);
}

.step-info-box--otp {
  margin-top: 0;
}

.step-info-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.step-info-text {
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
  margin: 0;
}

.step-info-email {
  font-weight: 600;
  color: #1a1d21;
}

.step-change-link {
  background: none;
  border: none;
  color: #0c9f6b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 4px;
  font-family: "DM Sans", sans-serif;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.step-back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 16px;
  padding: 8px;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  transition: color 0.2s ease;
}

.step-back-link:hover {
  color: #0c9f6b;
}

.otp-input-field {
  margin-bottom: 8px;
}

.otp-input-field :deep(.v-otp-input__content) {
  gap: 8px;
}

.otp-input-field :deep(.v-field) {
  border-radius: 12px !important;
  background: rgba(255, 255, 255, 0.55);
}

.sign-in-title {
  font-family: "Sora", "DM Sans", sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #1a1d21;
  line-height: 1.25;
  margin-bottom: 6px;
  letter-spacing: -0.02em;
}

.sign-in-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.auth-mode-tabs {
  display: flex;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 999px;
  padding: 3px;
  margin-bottom: 20px;
  gap: 4px;
  position: relative;
  z-index: 1;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.auth-mode-tab {
  flex: 1;
  border: none;
  background: transparent;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "DM Sans", sans-serif;
  text-transform: none;
  letter-spacing: 0.02em;
}

.auth-mode-tab--active {
  background: #ffffff;
  color: #0a876e;
  box-shadow:
    0 4px 16px rgba(12, 159, 107, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 1);
  font-weight: 600;
}

.sign-in-window {
  overflow: visible;
  position: relative;
  z-index: 1;
}

.sign-in-form {
  margin-bottom: 8px;
}

.field-group {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.field-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.forgot-link {
  background: none;
  border: none;
  color: #0c9f6b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-family: "DM Sans", sans-serif;
  text-transform: none;
  letter-spacing: 0.02em;
}

.forgot-link:hover {
  color: #0a876e;
}

.sign-in-field :deep(.v-field) {
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: box-shadow 0.2s ease, background 0.2s ease;
}

.sign-in-field :deep(.v-field--focused) {
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 0 3px rgba(12, 159, 107, 0.12);
}

.sign-in-field :deep(.v-field__outline) {
  --v-field-border-opacity: 0.2;
}

.sign-in-field :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 0.55;
  color: #0c9f6b;
}

.remember-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 999px;
  padding: 4px 12px 4px 6px;
  margin-bottom: 18px;
}

.remember-switch {
  flex-shrink: 0;
}

.remember-text {
  font-size: 13px;
  color: #374151;
}

.sign-in-btn {
  font-weight: 500 !important;
  font-size: 14px !important;
  letter-spacing: 0.02em;
  text-transform: none !important;
  height: 42px !important;
  box-shadow:
    0 6px 20px rgba(12, 159, 107, 0.26),
    inset 0 1px 0 rgba(255, 255, 255, 0.25) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.sign-in-btn:hover {
  transform: translateY(-2px);
  box-shadow:
    0 12px 36px rgba(12, 159, 107, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
}

.sign-in-btn:active {
  transform: translateY(0);
}

.step-hint {
  font-size: 14px;
  color: #0c9f6b;
  text-align: center;
  margin-bottom: 16px;
}

.step-hint--small {
  font-size: 12px;
  margin-top: 8px;
  margin-bottom: 0;
}

.step-actions {
  display: flex;
  align-items: center;
  margin-top: 20px;
}
</style>
<style>
.gradient-bg2 {
  background: -webkit-linear-gradient(145deg, #0a876e 0%, #0fc566 100%);
  background: -moz-linear-gradient(145deg, #0a876e 0%, #0fc566 100%);
  background: -o-linear-gradient(145deg, #0a876e 0%, #0fc566 100%);
  background: -ms-linear-gradient(145deg, #0a876e 0%, #0fc566 100%);
  background: linear-gradient(145deg, #0a876e 0%, #0fc566 100%);
}
input:-webkit-autofill {
  background-color: transparent !important;
  -webkit-box-shadow: 0 0 0px 1000px white inset !important;
  transition: background-color 5000s ease-in-out 0s;
}
::v-deep(.v-input__control) {
  font-size: 12px !important;
}
</style>
